import React, { useState, useEffect } from 'react';
import Picker from 'react-mobile-picker';
import './MobileDatePicker.css';

interface MobileDatePickerProps {
  value: Date;
  onChange: (date: Date) => void;
  isOpen: boolean;
  onClose: () => void;
}

const MobileDatePicker: React.FC<MobileDatePickerProps> = ({
  value,
  onChange,
  isOpen,
  onClose
}) => {
  // 生成年份选项（从2000年到当前年份）
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2000 + 1 }, (_, i) => (2000 + i).toString());
  
  const [pickerValue, setPickerValue] = useState({
    year: value.getFullYear().toString(),
    month: (value.getMonth() + 1).toString().padStart(2, '0'),
    day: value.getDate().toString().padStart(2, '0')
  });
  
  // 生成月份选项，限制未来月份
  const getMonthsForYear = (year: number) => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    
    // 如果是当前年份，只显示到当前月份
    if (year === currentYear) {
      return Array.from({ length: currentMonth }, (_, i) => (i + 1).toString().padStart(2, '0'));
    }
    
    return Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  };
  
  const months = getMonthsForYear(parseInt(pickerValue.year));
  
  // 根据年月生成日期选项，限制未来日期
  const getDaysInMonth = (year: number, month: number) => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1;
    const currentDay = today.getDate();
    
    // 如果是当前年月，只显示到今天
    if (year === currentYear && month === currentMonth) {
      return Array.from({ length: currentDay }, (_, i) => (i + 1).toString().padStart(2, '0'));
    }
    
    return Array.from({ length: daysInMonth }, (_, i) => (i + 1).toString().padStart(2, '0'));
  };
  
  // 当外部value变化时更新picker值
  useEffect(() => {
    setPickerValue({
      year: value.getFullYear().toString(),
      month: (value.getMonth() + 1).toString().padStart(2, '0'),
      day: value.getDate().toString().padStart(2, '0')
    });
  }, [value]);
  
  // 获取当前选择年月的天数
  const days = getDaysInMonth(parseInt(pickerValue.year), parseInt(pickerValue.month));
  
  // 处理picker值变化
  const handlePickerChange = (newValue: any, key: string) => {
    let updatedValue = { ...newValue };
    
    // 如果年份变化，需要检查月份是否有效
    if (key === 'year') {
      const newMonths = getMonthsForYear(parseInt(updatedValue.year));
      const currentMonth = parseInt(updatedValue.month);
      
      // 如果当前选择的月份超过了新年份的最大月份，调整为最大月份
      if (currentMonth > newMonths.length) {
        updatedValue.month = newMonths.length.toString().padStart(2, '0');
      }
    }
    
    // 如果年份或月份变化，需要检查日期是否有效
    if (key === 'year' || key === 'month') {
      const newDays = getDaysInMonth(parseInt(updatedValue.year), parseInt(updatedValue.month));
      const currentDay = parseInt(updatedValue.day);
      
      // 如果当前选择的日期超过了新月份的最大天数，调整为最大天数
      if (currentDay > newDays.length) {
        updatedValue.day = newDays.length.toString().padStart(2, '0');
      }
    }
    
    setPickerValue(updatedValue);
  };
  
  // 确认选择
  const handleConfirm = () => {
    const selectedDate = new Date(
      parseInt(pickerValue.year),
      parseInt(pickerValue.month) - 1,
      parseInt(pickerValue.day)
    );
    onChange(selectedDate);
    onClose();
  };
  
  // 取消选择
  const handleCancel = () => {
    // 重置为原始值
    setPickerValue({
      year: value.getFullYear().toString(),
      month: (value.getMonth() + 1).toString().padStart(2, '0'),
      day: value.getDate().toString().padStart(2, '0')
    });
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="mobile-date-picker-overlay">
      <div className="mobile-date-picker-modal">
        {/* 头部按钮 */}
        <div className="mobile-date-picker-header">
          <button 
            className="mobile-date-picker-cancel"
            onClick={handleCancel}
          >
            取消
          </button>
          <div className="mobile-date-picker-title">
            选择日期
          </div>
          <button 
            className="mobile-date-picker-confirm"
            onClick={handleConfirm}
          >
            确认
          </button>
        </div>
        
        {/* 滚动选择器 */}
        <div className="mobile-date-picker-content">
          <Picker
            value={pickerValue}
            onChange={handlePickerChange}
            height={216}
            itemHeight={36}
            wheelMode="natural"
          >
            {/* 年份列 */}
            <Picker.Column name="year">
              {years.map(year => (
                <Picker.Item key={year} value={year}>
                  {({ selected }) => (
                    <div className={`picker-item ${selected ? 'selected' : ''}`}>
                      {year}年
                    </div>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
            
            {/* 月份列 */}
            <Picker.Column name="month">
              {months.map(month => (
                <Picker.Item key={month} value={month}>
                  {({ selected }) => (
                    <div className={`picker-item ${selected ? 'selected' : ''}`}>
                      {month}月
                    </div>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
            
            {/* 日期列 */}
            <Picker.Column name="day">
              {days.map(day => (
                <Picker.Item key={day} value={day}>
                  {({ selected }) => (
                    <div className={`picker-item ${selected ? 'selected' : ''}`}>
                      {day}日
                    </div>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
          </Picker>
        </div>
      </div>
    </div>
  );
};

export default MobileDatePicker;